import { useEffect, useState } from 'react';

import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Button from '@zenbusiness/zui/Button';
import Chip from '@zenbusiness/zui/Chip';
import EmptyState from '@zenbusiness/zui/EmptyState';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Heading from '@zenbusiness/zui/Heading';
import IconButton from '@zenbusiness/zui/IconButton';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Snackbar from '@zenbusiness/zui/Snackbar';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import { accountSettingsSecurity, login } from '#client/constants/routes';
import { useMfaMethods } from '#client/hooks/useMfaMethods';

import { METHOD_TYPES, RENDERED_METHOD_TYPES } from './constants';
import { getRemovalBlockedReason } from './removalRules';
import * as Styled from './TwoStepVerification.style';

import { RemoveMfaMethodDialog } from '../RemoveMfaMethodDialog';

export const LOCALIZED_TEXT = {
  title: 'dashboard.accountSettings.twoStepVerification.title',
  on: 'dashboard.accountSettings.twoStepVerification.on',
  emptyStateTitle: 'dashboard.accountSettings.twoStepVerification.emptyState.title',
  emptyStateDescription: 'dashboard.accountSettings.twoStepVerification.emptyState.description',
  emptyStateSetUp: 'dashboard.accountSettings.twoStepVerification.emptyState.setUp',
  add: 'dashboard.accountSettings.twoStepVerification.add',
  added: 'dashboard.accountSettings.twoStepVerification.added',
  lastUsed: 'dashboard.accountSettings.twoStepVerification.lastUsed',
  remove: 'dashboard.accountSettings.twoStepVerification.remove',
  methodAdded: 'dashboard.accountSettings.twoStepVerification.methodAdded',
};

const formatMfaDate = (isoDate) => (isoDate ? format(parseISO(isoDate), 'MMM d, yyyy') : null);

const maskPhoneNumber = (phoneNumber) => (phoneNumber ? `••• ••• ${phoneNumber.slice(-4)}` : null);

// The "Add"/"Set it up" flow doesn't preselect a factor type — Auth0's enrollment flow
// always offers a choice of whichever methods the user doesn't have yet (confirmed Sep 15).
const enrollmentUrl = `${login}?mfa_enroll=true&redirectTo=${encodeURIComponent(accountSettingsSecurity)}`;

// Enrollment happens in Auth0 and comes back with a full page load, so the ids enrolled before
// leaving are kept here to tell which method is new and confirm it.
const ENROLLED_IDS_KEY = 'twoStepVerification.enrolledIds';

const readEnrolledIdsBeforeAdd = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ENROLLED_IDS_KEY));
  } catch {
    return null;
  }
};

function TwoStepVerification() {
  const { formatMessage: fm } = useIntl();
  const { methods, requireMfa, loading, error, refetch } = useMfaMethods();
  const navigate = useNavigate();
  const theme = useTheme();
  const [methodToRemove, setMethodToRemove] = useState(null);
  const [enrolledIdsBeforeAdd] = useState(readEnrolledIdsBeforeAdd);
  const [addedToastDismissed, setAddedToastDismissed] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.removeItem(ENROLLED_IDS_KEY);
    } catch {
      // Without storage the confirmation just doesn't show.
    }
  }, []);

  if (loading) {
    return (
      <Styled.Card2fa>
        <Skeleton height="28px" width="40%" />
        <Skeleton height="80px" />
      </Styled.Card2fa>
    );
  }

  // A failed read must not look like "no methods enrolled" — that would invite the user to
  // re-enroll factors they already have.
  if (error) {
    return (
      <Styled.Card2fa>
        <ErrorState />
      </Styled.Card2fa>
    );
  }

  // Only a confirmed, non-recovery factor of a type we render actually satisfies MFA. customers-api's
  // `exists` flag also counts pending enrollments and recovery codes, so it can't drive On/Off, and a
  // type without a row would show On while leaving the user nothing to manage.
  const usableMethods = methods.filter((m) => m.confirmed && !m.isRecoveryMethod && RENDERED_METHOD_TYPES.has(m.type));
  // The removal rules count every confirmed method, as customers-api does.
  const confirmedMethods = methods.filter((m) => m.confirmed);

  const startEnrollment = () => {
    try {
      sessionStorage.setItem(ENROLLED_IDS_KEY, JSON.stringify(usableMethods.map(({ id }) => id)));
    } catch {
      // Without storage the confirmation just doesn't show.
    }
    navigate(enrollmentUrl);
  };

  const addedMethod = enrolledIdsBeforeAdd && usableMethods.find(({ id }) => !enrolledIdsBeforeAdd.includes(id));
  const addedMethodType = addedMethod && METHOD_TYPES.find(({ types }) => types.includes(addedMethod.type));

  // With no methods there's nothing to list, so the card offers the single general setup flow
  // (the customer picks the method in Auth0), as aligned on Sep 15.
  if (usableMethods.length === 0) {
    return (
      <Styled.Card2fa>
        <EmptyState
          title={fm({ id: LOCALIZED_TEXT.emptyStateTitle })}
          description={fm({ id: LOCALIZED_TEXT.emptyStateDescription })}
          slots={{ image: Styled.EmptyStateIcon }}
          slotProps={{
            primaryAction: {
              label: fm({ id: LOCALIZED_TEXT.emptyStateSetUp }),
              variant: 'contained',
              color: 'primary',
              clickstreamProps: { appSection: 'account', elementName: 'two_step_verification_set_up' },
              onClick: startEnrollment,
            },
          }}
        />
      </Styled.Card2fa>
    );
  }

  return (
    <Styled.Card2fa>
      <Styled.HeaderRow>
        <Heading component="h2" size="lg">
          {fm({ id: LOCALIZED_TEXT.title })}
        </Heading>
        <Chip color="success" label={fm({ id: LOCALIZED_TEXT.on })} size="small" />
      </Styled.HeaderRow>
      <Styled.MethodList>
        {METHOD_TYPES.map(({ key, types, label: labelId, name: nameId, description, Icon }) => {
          const label = fm({ id: labelId });
          const method = usableMethods.find((m) => types.includes(m.type));
          const enrolled = Boolean(method);
          const detail = key === 'sms' ? maskPhoneNumber(method?.phoneNumber) : method?.name;
          const added = formatMfaDate(method?.createdAt);
          const lastUsed = formatMfaDate(method?.lastAuth);
          const removalBlockedReason = enrolled ? getRemovalBlockedReason(method, confirmedMethods, requireMfa) : null;

          return (
            <Styled.MethodRow key={key}>
              <Styled.MethodIcon
                sx={{
                  backgroundColor: enrolled ? theme.palette.background.blue.light : theme.palette.background.grey.dark,
                }}
              >
                <Icon fontSize="small" />
              </Styled.MethodIcon>
              <Styled.MethodText>
                <Text component="p" size="md" weight="medium" color="primary">
                  {label}
                </Text>
                {enrolled ? (
                  <>
                    {detail && (
                      <Text component="p" size="sm" color="secondary">
                        {detail}
                      </Text>
                    )}
                    {(added || lastUsed) && (
                      <Text component="p" size="sm" color="secondary">
                        {added && fm({ id: LOCALIZED_TEXT.added }, { date: added })}
                        {added && lastUsed && ' · '}
                        {lastUsed && fm({ id: LOCALIZED_TEXT.lastUsed }, { date: lastUsed })}
                      </Text>
                    )}
                    {removalBlockedReason && (
                      <Text component="p" size="sm" color="secondary">
                        {fm({ id: removalBlockedReason.id }, removalBlockedReason.values)}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text component="p" size="sm" color="secondary">
                    {fm({ id: description })}
                  </Text>
                )}
              </Styled.MethodText>
              {enrolled ? (
                <IconButton
                  label={fm({ id: LOCALIZED_TEXT.remove }, { method: label })}
                  data-testid={`remove-${key}`}
                  disabled={Boolean(removalBlockedReason)}
                  onClick={() => setMethodToRemove({ ...method, label, detail, name: fm({ id: nameId }) })}
                  icon={<DeleteRoundedIcon />}
                />
              ) : (
                <Button
                  data-testid={`add-${key}`}
                  clickstreamProps={{ appSection: 'account', elementName: 'two_step_verification_add' }}
                  onClick={startEnrollment}
                  size="sm"
                  variant="text"
                  color="primary"
                >
                  {fm({ id: LOCALIZED_TEXT.add })}
                </Button>
              )}
            </Styled.MethodRow>
          );
        })}
      </Styled.MethodList>
      {methodToRemove && (
        <RemoveMfaMethodDialog
          isOpen
          method={methodToRemove}
          // Refetch on every close, not only after a removal: a refusal or notFound means the list
          // was stale, and the fresh one lets the card block the removal upfront.
          onClose={() => {
            setMethodToRemove(null);
            refetch();
          }}
        />
      )}
      {addedMethodType && !addedToastDismissed && (
        <Snackbar
          open
          variant="action"
          title={fm({ id: LOCALIZED_TEXT.methodAdded }, { method: fm({ id: addedMethodType.label }) })}
          dismissable
          autoHideDuration={6000}
          onClose={() => setAddedToastDismissed(true)}
        />
      )}
    </Styled.Card2fa>
  );
}

export default TwoStepVerification;
